import React, { FC, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { filter, isEmpty } from 'lodash-es';
import { ContactStatus, UnsubscribedContactsOrderBy } from '@dev/ee-api/ee-api-models';
import { CustomTableHeadersProps, FilterIcon, SimplePopover, Tag } from '@dev/ee-components';

import { SortingEnum } from '@/services/dictionaries/SortingEnum';
import { ContactsUnsubscribedParams } from '@/services/ElasticApi';
import { GetContactsUnsubscribed, getUnsubscribeContactsStore } from '@/store/contacts/actions';
import { calculateOffset } from '@/services/tools/handleUrlParams';
import { UnsubscribedContactsStatusCounts } from '@/store/contacts/types';
import { FilterContainer, FilterLabel } from '@/components/Campaigns/assets/EventFilter/styledComponents';

import { CustomPopoverBodyProps, FilterOptions } from '../../UnsubscribedInterface';

const CustomPopoverBody: FC<CustomPopoverBodyProps> = (props: CustomPopoverBodyProps): JSX.Element => {
    const { label, count } = props?.element || {};

    return (
        <FilterContainer>
            <FilterLabel>{label}</FilterLabel>
            {count && <Tag label={count} clickable={false} />}
        </FilterContainer>
    );
};

const getStatusPopoverOptions = (statusCount: UnsubscribedContactsStatusCounts, t: TFunction): FilterOptions[] => {
    const possibleStatus = [
        {
            label: t('unsubscribed'),
            value: ContactStatus.Unsubscribed,
            count: statusCount?.unsubscribed,
        },
        {
            label: t('complained'),
            value: ContactStatus.Abuse,
            count: statusCount?.abused,
        },
    ];

    return filter(possibleStatus, (status): boolean => status.count > 0);
};

const StatusFilters: FC = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { pagination, statusCount } = useSelector(getUnsubscribeContactsStore);

    const options = getStatusPopoverOptions(statusCount, t);

    if (isEmpty(options)) {
        return null;
    }

    const onCheckboxClickStatusFilters = (ev: any, selectedItems: ContactStatus[]): void => {
        const offset = calculateOffset(pagination.activePage, pagination.perPage);
        const requestParams: ContactsUnsubscribedParams = {
            limit: pagination.perPage,
            offset,
            statuses: selectedItems,
            from: pagination.from,
            to: pagination.to,
            email: pagination.email,
            orderBy: pagination.orderBy,
        };

        dispatch(GetContactsUnsubscribed(requestParams));
    };
    const renderCustomBody = (element: any): JSX.Element => <CustomPopoverBody element={element} />;

    return (
        <SimplePopover
            type="icon"
            options={options}
            defaultCheckboxValue={[]}
            enableCheckbox
            onCheckboxClick={onCheckboxClickStatusFilters}
            customDropdownBody={renderCustomBody}
            popperProps={{
                disablePortal: false,
            }}
        >
            <FilterIcon />
        </SimplePopover>
    );
};

export const useTableHeaders = (requestParams: ContactsUnsubscribedParams): CustomTableHeadersProps[] => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { contactsList } = useSelector(getUnsubscribeContactsStore);
    const [dateSortState, setDateSortState] = useState(UnsubscribedContactsOrderBy.DateDesc);
    const [feedbackSortState, setFeedbackSortState] = useState(UnsubscribedContactsOrderBy.FeedbackDesc);

    const sortFunctionDateUnsubscribed = (): void => {
        const isDateDescOrder = dateSortState === UnsubscribedContactsOrderBy.DateDesc;
        dispatch(
            GetContactsUnsubscribed({
                ...requestParams,
                orderBy: isDateDescOrder ? UnsubscribedContactsOrderBy.DateAsc : UnsubscribedContactsOrderBy.DateDesc,
            })
        );

        setDateSortState(isDateDescOrder ? UnsubscribedContactsOrderBy.DateAsc : UnsubscribedContactsOrderBy.DateDesc);
    };

    const sortFunctionFeedback = (): void => {
        const isFeedbackDescOrder = feedbackSortState === UnsubscribedContactsOrderBy.FeedbackDesc;
        setFeedbackSortState(
            isFeedbackDescOrder ? UnsubscribedContactsOrderBy.FeedbackAsc : UnsubscribedContactsOrderBy.FeedbackDesc
        );

        dispatch(
            GetContactsUnsubscribed({
                ...requestParams,
                orderBy: isFeedbackDescOrder
                    ? UnsubscribedContactsOrderBy.FeedbackAsc
                    : UnsubscribedContactsOrderBy.FeedbackDesc,
            })
        );
    };

    const isData = !isEmpty(contactsList);

    return [
        {
            id: 'email',
            label: t('email'),
        },
        {
            id: 'status',
            label: t('status'),
            filterIcon: <StatusFilters />,
        },
        {
            id: 'dateUnsubscribed',

            label: t('date'),
            sortable: isData,
            direction:
                dateSortState === UnsubscribedContactsOrderBy.DateDesc
                    ? (SortingEnum.Asc as const)
                    : (SortingEnum.Desc as const),
            sortFunction: isData ? sortFunctionDateUnsubscribed : null,
            headerAlign: 'right',
        },
        {
            id: 'unsubscribemethod',
            label: t('unsubscribemethod'),
            columnAlign: 'left',
            headerAlign: 'left',
        },
        {
            id: 'unsubscribereason',
            label: t('unsubscribereason'),
            columnAlign: 'left',
            headerAlign: 'left',
        },
        {
            id: 'feedback',
            label: t('feedback'),
            sortable: isData,
            direction:
                feedbackSortState === UnsubscribedContactsOrderBy.FeedbackDesc
                    ? (SortingEnum.Asc as const)
                    : (SortingEnum.Desc as const),
            sortFunction: isData ? sortFunctionFeedback : null,
        },
    ];
};
